import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';

// Real GDP Growth comparison data - Central Asia & Silk Road (World Bank / IMF 2024)
const gdpGrowthData = [
  { year: '2018', uzb: 5.4, kaz: 4.1, geo: 4.8, emerging: 4.5 },
  { year: '2019', uzb: 5.8, kaz: 4.5, geo: 5.0, emerging: 3.6 },
  { year: '2020', uzb: 1.9, kaz: -2.5, geo: -6.8, emerging: -1.8 },
  { year: '2021', uzb: 7.4, kaz: 4.3, geo: 10.5, emerging: 6.9 },
  { year: '2022', uzb: 5.7, kaz: 3.2, geo: 10.1, emerging: 4.0 },
  { year: '2023', uzb: 6.3, kaz: 5.1, geo: 7.5, emerging: 4.1 },
  { year: '2024', uzb: 6.5, kaz: 5.2, geo: 7.0, emerging: 4.2 },
];

interface GrowthComparisonPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const GrowthComparisonPanel: React.FC<GrowthComparisonPanelProps> = ({ isMinimized, onMinimize }) => {
  return (
    <Panel title="GROWTH COMPARISON" subtitle="REAL GDP % YOY" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full">
        {/* Chart */}
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={gdpGrowthData} margin={{ top: 10, right: 15, bottom: 5, left: -10 }}>
              <XAxis
                dataKey="year"
                tick={{ fontSize: 9, fill: '#6b7280' }}
                axisLine={{ stroke: '#1e1e26' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
                width={35}
                domain={[-4, 10]}
                tickFormatter={(value) => `${value}%`}
              />
              <ReferenceLine y={0} stroke="#374151" strokeDasharray="3 3" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #00d26a',
                  borderRadius: '6px',
                  fontSize: '11px',
                  padding: '10px 14px',
                  boxShadow: '0 4px 20px rgba(0, 210, 106, 0.15)',
                }}
                labelStyle={{ color: '#ffffff', fontWeight: 'bold', fontSize: '12px', marginBottom: '6px' }}
                itemStyle={{ padding: '2px 0' }}
                formatter={(value: number, name: string) => {
                  const labels: Record<string, string> = {
                    uzb: 'Uzbekistan',
                    kaz: 'Kazakhstan',
                    geo: 'Georgia',
                    emerging: 'EM Average'
                  };
                  return [`${value}%`, labels[name] || name];
                }}
                cursor={{ strokeDasharray: '3 3', stroke: '#00d26a' }}
              />
              <Line
                type="monotone"
                dataKey="uzb"
                name="uzb"
                stroke="#00d26a"
                strokeWidth={2.5}
                dot={{ fill: '#00d26a', r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="kaz"
                name="kaz"
                stroke="#6b7280"
                strokeWidth={1.5}
                dot={{ fill: '#6b7280', r: 2 }}
              />
              <Line
                type="monotone"
                dataKey="geo"
                name="geo"
                stroke="#00a3ff"
                strokeWidth={1.5}
                dot={{ fill: '#00a3ff', r: 2 }}
              />
              <Line
                type="monotone"
                dataKey="emerging"
                name="emerging"
                stroke="#ffb800"
                strokeWidth={1.5}
                strokeDasharray="5 5"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 pt-2 shrink-0 border-t border-terminal-border">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-data-green rounded" />
            <span className="text-xs text-data-green font-medium">UZB</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-gray-500 rounded" />
            <span className="text-xs text-gray-500">KAZ</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-data-blue rounded" />
            <span className="text-xs text-data-blue">GEO</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-data-amber rounded" style={{ borderBottom: '1px dashed #ffb800' }} />
            <span className="text-xs text-data-amber">EM Avg</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.IMF} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default GrowthComparisonPanel;
